import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import WebAssetOutlinedIcon from '@mui/icons-material/WebAssetOutlined';
import {Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Paper, Stack, Typography} from '@mui/material';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'tolgee';

import {HelpButton} from '~components/header/HelpButton';
import {QualityAgent} from '~components/quality-agent/QualityAgent';
import {SparklesIcon} from 'src/icons';
import {AGENT_PANEL_GLOW, AGENT_TILE_GRADIENT, ASK_AI_PANEL_MENU_Z_INDEX, ASK_AI_PANEL_Z_INDEX} from './askAiAgents';
import {AskAiLayout, useAskAi} from './AskAiContext';
import {AskAiWidgetController, loadAskAiWidget} from './askAiWidget';
import {useFloatingBottomOffset, useLayoutNavWidth} from './useLayoutMetrics';

const PANEL_WIDTH = 440;
const SIDEBAR_WIDTH_CSS_VAR = '--ask-ai-sidebar-width';

export function AskAiPanel() {
  const {t} = useTranslation('common');
  const {isEnabled, isQualityEnabled, isOpen, agent, layout, closePanel, setLayout} = useAskAi();
  const bottomOffset = useFloatingBottomOffset();
  const navWidth = useLayoutNavWidth();
  const containerRef = useRef<HTMLDivElement>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [qualityMounted, setQualityMounted] = useState(false);
  const [layoutMenuAnchor, setLayoutMenuAnchor] = useState<HTMLElement | null>(null);
  const controllerRef = useRef<AskAiWidgetController | null>(null);

  const isAgentEnabled = agent === 'vision' ? isEnabled : isQualityEnabled;
  const showsPanel = isOpen && isAgentEnabled;

  useEffect(() => {
    if (!isEnabled) {
      controllerRef.current?.unmount();
      controllerRef.current = null;
      return;
    }
    if (!isOpen || agent !== 'vision' || controllerRef.current || !containerRef.current) {
      return;
    }
    const container = containerRef.current;
    let cancelled = false;
    setLoadFailed(false);
    void loadAskAiWidget().then((controller) => {
      if (cancelled) {
        return;
      }
      if (!controller) {
        setLoadFailed(true);
        return;
      }
      controller.mount(container);
      controllerRef.current = controller;
    });
    return () => {
      cancelled = true;
    };
  }, [isEnabled, isOpen, agent]);

  useEffect(
    () => () => {
      controllerRef.current?.unmount();
      controllerRef.current = null;
    },
    [],
  );

  useEffect(() => {
    if (isOpen && agent === 'quality' && isQualityEnabled) {
      setQualityMounted(true);
    }
  }, [isOpen, agent, isQualityEnabled]);

  const reservesSidebarSpace = showsPanel && layout === 'sidebar';

  useEffect(() => {
    if (!reservesSidebarSpace) {
      return;
    }
    document.documentElement.style.setProperty(SIDEBAR_WIDTH_CSS_VAR, `${PANEL_WIDTH}px`);
    return () => {
      document.documentElement.style.removeProperty(SIDEBAR_WIDTH_CSS_VAR);
    };
  }, [reservesSidebarSpace]);

  if (!isEnabled && !isQualityEnabled) {
    return null;
  }

  const layoutOptions: {value: AskAiLayout; label: string}[] = [
    {value: 'sidebar', label: t('askAiLayoutSidebar', 'Sidebar')},
    {value: 'floating', label: t('askAiLayoutFloating', 'Floating')},
    {value: 'fullscreen', label: t('askAiLayoutFullscreen', 'Fullscreen')},
  ];

  const layoutSx =
    layout === 'sidebar'
      ? {top: 0, right: 0, bottom: 0, width: PANEL_WIDTH, height: '100vh', borderRadius: 0}
      : layout === 'fullscreen'
        ? {top: 0, right: 0, bottom: 0, left: navWidth, height: '100vh', borderRadius: 0}
        : {
            right: 24,
            bottom: bottomOffset,
            width: PANEL_WIDTH,
            height: `min(720px, calc(100vh - ${bottomOffset + 96}px))`,
            borderRadius: 2,
          };

  const title = agent === 'vision' ? t('visionAgent', 'Vision Agent') : t('qualityAgent', 'Process Agent');

  return (
    <Paper
      elevation={layout === 'sidebar' ? 0 : 8}
      sx={(theme) => ({
        position: 'fixed',
        display: showsPanel ? 'flex' : 'none',
        flexDirection: 'column',
        overflow: 'hidden',
        zIndex: ASK_AI_PANEL_Z_INDEX,
        backgroundColor: theme.palette.common.white,
        ...(layout === 'sidebar' && {borderLeft: `1px solid ${theme.palette.maddox.black20}`}),
        ...layoutSx,
      })}
      data-testid='ask-ai-panel'
      data-agent={agent}
    >
      <Stack
        direction='row'
        alignItems='center'
        gap={1}
        sx={{
          background: AGENT_PANEL_GLOW[agent],
          paddingX: 2,
          paddingTop: 2,
          paddingBottom: 1.5,
        }}
      >
        <Box
          sx={(theme) => ({
            width: 30,
            height: 30,
            borderRadius: 1,
            background: AGENT_TILE_GRADIENT[agent],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.common.white,
            flexShrink: 0,
            '& svg path': {fill: 'currentColor'},
          })}
        >
          <SparklesIcon />
        </Box>
        <Typography variant='subtitle1' fontWeight={600}>
          {title}
        </Typography>
        <Box sx={{flex: 1}} />
        {layout === 'fullscreen' && <HelpButton idPrefix='ask-ai-panel-help' menuZIndex={ASK_AI_PANEL_MENU_Z_INDEX} />}
        <IconButton
          size='small'
          onClick={(event) => setLayoutMenuAnchor(event.currentTarget)}
          data-testid='ask-ai-panel-layout'
        >
          <WebAssetOutlinedIcon fontSize='small' />
        </IconButton>
        <IconButton size='small' onClick={closePanel} data-testid='ask-ai-panel-close'>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Stack>
      <Menu
        anchorEl={layoutMenuAnchor}
        open={Boolean(layoutMenuAnchor)}
        onClose={() => setLayoutMenuAnchor(null)}
        sx={{zIndex: ASK_AI_PANEL_MENU_Z_INDEX}}
      >
        {layoutOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => {
              setLayout(option.value);
              setLayoutMenuAnchor(null);
            }}
          >
            <ListItemIcon>{layout === option.value && <CheckIcon fontSize='small' />}</ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
      <Box sx={{flex: 1, minHeight: 0}}>
        {agent === 'vision' && loadFailed && (
          <Typography variant='body2' color='text.secondary' sx={{padding: 3}}>
            {t('askAiUnavailable', 'Ask AI is currently unavailable. Please try again later.')}
          </Typography>
        )}
        <div
          ref={containerRef}
          data-aa-layout={layout}
          style={{height: '100%', display: agent === 'vision' && !loadFailed ? undefined : 'none'}}
        />
        {qualityMounted && (
          <Box sx={{height: '100%', display: agent === 'quality' ? 'flex' : 'none'}} data-testid='ask-ai-quality-agent'>
            <QualityAgent panelLayout={layout} />
          </Box>
        )}
      </Box>
    </Paper>
  );
}
